import { gql } from 'graphql-request';
import _ from 'lodash';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import AppError from '../../../../app/features/error/services/AppError';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import IInvestExpertiseFilters from '../../interfaces/expertise/IInvestExpertiseFilters';
import IFundTypeHistoryPeriod from '../../interfaces/expertise/history/IFundTypeHistoryPeriod';
import { useAppNavigationContext } from '../../../../app/features/navigation/providers/AppNavigationProvider';

const useInvestExpertiseHistoryStatsByPeriods = (filters: IInvestExpertiseFilters) => {
	const auth = useAuthContext();

	const navigationContext = useAppNavigationContext();
	const graphQLClient = useGraphQLClient();
	const filter = {
		typeTag: filters.typeTag,
		fundIds: filters.fundIds,
		investorIds: filters.investorIds,
		currency: navigationContext.currentCurrency,
	};
	const { data, isFetching } = useQuery<IFundTypeHistoryPeriod[], Error>(
		['fundTypeHistoryPeriods', filter],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($typeTag: String, $fundIds: [Int], $currency: String, $investorIds: [Int]) {
							fundTypeHistoryPeriods(
								typeTag: $typeTag
								fundIds: $fundIds
								currency: $currency
								investorIds: $investorIds
							) {
								periods {
									year
									quarter
									name
									stat {
										currency
										cumulatedDistributions
										cumulatedCalledCapital
										cumulatedNetCalledCapital
										cumulatedCallableCapital
										cumulatedCashflows
										totalValue
										totalCapitalCalls
										tvpi
									}
									funds {
										currency
										fundId
										fundName
										fundIsRealized
										calledCapital
										commitments
									}
								}
							}
						}
					`,
					filter,
				)) as { fundTypeHistoryPeriods: { periods: IFundTypeHistoryPeriod[] } };

				// On ajoute 2 années au début en plus
				// On ajoute tous les quarters manquants pour faire des années complètes
				const periods = _.orderBy(data.fundTypeHistoryPeriods.periods, ['year', 'quarter'], ['asc', 'asc']);

				const firstPeriod = periods ? periods[0] : null;
				const lastPeriod = periods ? periods[periods.length - 1] : null;
				if (firstPeriod && lastPeriod) {
					for (let y = firstPeriod.year - 2; y <= lastPeriod.year; y += 1) {
						for (let q = 1; q <= 4; q += 1) {
							const existPeriod = periods.find((x) => x.year === y && x.quarter === q);
							if (!existPeriod) {
								periods.push({
									year: y,
									quarter: q,
									name: y + ' ' + 'Q' + q,
									stat: {
										cumulatedDistributions: null,
										cumulatedCalledCapital: null,
										cumulatedNetCalledCapital: null,
										cumulatedCallableCapital: null,
										cumulatedCashflows: null,
										totalValue: null,
										totalCapitalCalls: null,
										tvpi: null,
										currency: filter.currency,
									},
									funds: [],
								});
							}
						}
					}
				}
				// Set round 2 to TVPI
				return periods
					.filter((x) => x.stat)
					.map((x) => ({
						...x,
						stat: {
							...x.stat,
							tvpi: x.stat?.tvpi ? _.round(x.stat?.tvpi, 2) : null,
						},
					}));
			} catch (e) {
				throw new AppError(e as Error, 'useInvestExpertiseHistoryStatsByPeriods');
			}
		},
		{
			enabled:
				!!filter.fundIds &&
				auth.currentCompany !== null &&
				auth.isInvestor === true &&
				auth.isAuthenticated &&
				navigationContext.currentCurrency !== null,
		},
	);
	const results = useMemo(() => (data ? (_.cloneDeep(data) as IFundTypeHistoryPeriod[]) : undefined), [data]);

	return {
		isFetching,
		results,
	};
};

export default useInvestExpertiseHistoryStatsByPeriods;
